import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";
import { breadcrumbLd } from "@/components/seo/jsonld";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Journal category labels — mirror catalog categories so URLs stay consistent
const CATEGORIES = [
  { slug: "ha-filler",       label: "HA Fillers",           description: "Hyaluronic acid dermal fillers." },
  { slug: "botox",           label: "Botulinum Toxins",     description: "Neurotoxins for clinical use." },
  { slug: "skin-booster",    label: "Skin Boosters",        description: "Bioregenerative skinboosters." },
  { slug: "body-filler",     label: "Body Fillers",         description: "Collagen biostimulators." },
  { slug: "lipolysis",       label: "Lipolytics",           description: "Injectable fat-dissolving solutions." },
  { slug: "exosome",         label: "Exosomes",             description: "Regenerative exosome therapy." },
  { slug: "iv-therapy",      label: "IV Therapy",           description: "IV / IM wellness injectables." },
  { slug: "lidocaine-cream", label: "Anesthetic Creams",    description: "Topical numbing for procedures." },
  { slug: "etc",             label: "Other Accessories",    description: "Threads, cannulas, ancillaries." },
];

export const Journal = () => {
  const [counts, setCounts] = useState({});         // {category: count}
  const [previews, setPreviews] = useState({});     // {category: post[]}
  const [latest, setLatest] = useState(null);       // featured (most recent)
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // 1) Latest 1 (featured banner)
        const latestRes = await axios.get(`${API}/journal/posts?limit=1`);
        setLatest((latestRes.data.posts || [])[0] || null);
        setTotal(latestRes.data.total || 0);

        // 2) Counts + previews per category in parallel
        const results = await Promise.all(
          CATEGORIES.map((c) =>
            axios.get(`${API}/journal/posts?category=${c.slug}&limit=3`)
              .then((r) => [c.slug, r.data])
              .catch(() => [c.slug, { total: 0, posts: [] }])
          )
        );
        const c = {};
        const p = {};
        for (const [slug, data] of results) {
          c[slug] = data.total || 0;
          p[slug] = data.posts || [];
        }
        setCounts(c);
        setPreviews(p);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <>
      <Seo
        title="Aesthetic Medicine Journal — Clinical Guides & Wholesale Insights"
        description="Editorial guides for clinics: injectable comparisons, sourcing verification, regulatory news, and wholesale procurement insights. New articles daily."
        keywords="aesthetic medicine journal, botox comparison guide, dermal filler protocols, wholesale procurement guide, clinic buyer guide, MOQ 5 wholesale, Korean botulinum toxin review, biostimulator clinical guide, aesthetic supply news"
        path="/journal"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Journal", url: "/journal" }])}
      />
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 pt-28 md:pt-32 pb-24" data-testid="journal-home">
        <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Journal</div>
        <h1 className="font-heading text-5xl md:text-7xl tracking-tight leading-[0.95] mb-6">
          <span className="block font-black">Field notes</span>
          <span className="block font-thin">from the industry.</span>
        </h1>
        <p className="text-base text-neutral-600 font-light max-w-2xl mb-12">
          Working clinicians&apos; notes on injectables, biostimulators, exosomes and the business of aesthetic medicine.
          {total > 0 && <> <span className="text-black font-medium">{total} articles published</span> across {CATEGORIES.length} categories.</>}
        </p>

        {loading ? (
          <div className="py-24 flex items-center justify-center text-neutral-500">
            <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading…
          </div>
        ) : (
          <>
            {/* Featured (latest article) */}
            {latest && (
              <Link
                to={`/journal/${latest.slug}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 pb-16 border-b border-neutral-200"
                data-testid={`journal-featured-${latest.id}`}
              >
                <div className="md:col-span-7 aspect-[16/10] overflow-hidden bg-neutral-100">
                  <img src={latest.image} alt={latest.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" />
                </div>
                <div className="md:col-span-5 flex flex-col gap-4 justify-center">
                  <div className="flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
                    <span>Latest</span>
                    <span className="w-1 h-1 bg-neutral-400 rounded-full" />
                    <span>{latest.reading_time_min} min read</span>
                  </div>
                  <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tight leading-tight group-hover:underline underline-offset-8">{latest.title}</h2>
                  <p className="text-base text-neutral-600 font-light leading-relaxed line-clamp-3">{latest.subtitle}</p>
                  <div className="inline-flex items-center gap-2 text-sm font-medium">
                    Read article <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            )}

            {/* Category hub: each category = a column with 3 latest previews + "Browse all" link */}
            <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight mb-8" data-testid="journal-categories-heading">Browse by category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-px gap-y-px border border-neutral-200" data-testid="journal-categories-grid">
              {CATEGORIES.map((cat) => {
                const count = counts[cat.slug] || 0;
                const items = previews[cat.slug] || [];
                return (
                  <div key={cat.slug} className="bg-white border-neutral-200 border-r border-b -mr-px -mb-px p-6 flex flex-col" data-testid={`journal-cat-card-${cat.slug}`}>
                    <Link to={`/journal/category/${cat.slug}`} className="group">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-medium mb-2">{count} {count === 1 ? "article" : "articles"}</div>
                      <h3 className="font-heading text-xl font-black tracking-tight group-hover:underline underline-offset-4">{cat.label}</h3>
                      <p className="text-xs text-neutral-500 font-light mt-1 mb-4">{cat.description}</p>
                    </Link>

                    {items.length === 0 ? (
                      <p className="text-xs text-neutral-400 font-light italic mt-2">No articles yet.</p>
                    ) : (
                      <ul className="space-y-3 mt-2">
                        {items.map((p) => (
                          <li key={p.id}>
                            <Link to={`/journal/${p.slug}`} className="flex gap-3 group" data-testid={`journal-cat-preview-${p.id}`}>
                              <img src={p.image} alt="" className="w-14 h-14 object-cover bg-neutral-100 shrink-0" loading="lazy" />
                              <div className="min-w-0 flex-1">
                                <h4 className="text-xs font-bold leading-tight line-clamp-2 group-hover:underline underline-offset-2">{p.title}</h4>
                                <p className="text-[10px] text-neutral-500 mt-1">{p.reading_time_min} min</p>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}

                    {count > 3 && (
                      <Link
                        to={`/journal/category/${cat.slug}`}
                        className="mt-4 inline-flex items-center gap-1 text-[11px] font-bold tracking-wide uppercase text-black hover:underline"
                        data-testid={`journal-cat-browse-${cat.slug}`}
                      >
                        View all {count} <ArrowUpRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
      <Footer />
    </>
  );
};
